import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncSourcesEnum,
} from '../../../../../../../../src/models/api/crmSyncModel';
import {Trans} from '../../../../../../../shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {Toggle} from '../../../../../../../shared/ui/src/components/booleanInputs/toggle';
import {Icon, IconName} from '../../../../../../../shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../../../../shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../../shared/ui/src/theme/userThemeProvider';
import {useCrmPageConfig} from './crmSyncsPageConfig/crmSyncsPageConfig';

/*
 * Props.
 */

export interface CrmSyncsSectionHeaderProps {
  isAdmin: boolean;
  isEnabled: boolean;
  source: CrmSyncSourcesEnum;
  resourceType: CrmSyncIntegrationResourceTypesEnum;
  isChangeDisabled: boolean;
  onChange: HandlerOf<boolean>;
}

/*
 * Styles.
 */

interface StyledSectionHeaderWrapperProps {
  $isEnabled: boolean;
  $isChangeDisabled: boolean;
}

const StyledSectionHeaderWrapperDiv = styled.div<StyledSectionHeaderWrapperProps>`
  display: flex;
  align-items: center;
  padding: ${(p) => (p.$isEnabled ? '21px 0 2px' : '21px 0')};
  width: 600px;
  ${(p) => p.$isChangeDisabled && addDisabledStyles()}
`;

interface SectionHeaderStyleProps {
  $isEnabled: boolean;
}

const StyledIconWrapperDiv = styled.div<SectionHeaderStyleProps>`
  display: flex;
  align-items: center;
  justify-content: space-around;
  height: 40px;
  width: 40px;
  border-radius: 50%;
  background-color: ${(p) => (p.$isEnabled ? p.theme.palette.blue.shade40 : p.theme.greys.shade30)};
`;

const StyledTitleAndDescriptionWrapperDiv = styled.div`
  margin-left: 16px;
  margin-right: auto;
`;

const StyledTitleDiv = styled.div<SectionHeaderStyleProps>`
  color: ${(p) => p.theme.greys.shade90};
  font-weight: ${fontWeights.semibold};
  font-size: 18px;
  line-height: 24px;
`;

const StyledDescriptionDiv = styled.div<SectionHeaderStyleProps>`
  color: ${(p) => p.theme.greys.shade70};
  font-size: 14px;
  line-height: 22px;
`;

interface StyledToggleWrapperDivProps {
  $isAdmin: boolean;
}

const StyledToggleWrapperDiv = styled.div<StyledToggleWrapperDivProps>`
  ${(p) => !p.$isAdmin && addDisabledStyles()}
`;

function addDisabledStyles() {
  return css`
    opacity: 0.4;
    pointer-events: none;
  `;
}

export const CrmSyncsSectionHeader: FC<CrmSyncsSectionHeaderProps> = (props) => {
  const {isAdmin, isEnabled, isChangeDisabled, source, resourceType, onChange} = props;
  const theme = useUserTheme();
  const {account, card} = useCrmPageConfig(source);
  const resourceConfig = resourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT ? account : card;
  return (
    <StyledSectionHeaderWrapperDiv $isEnabled={isEnabled} $isChangeDisabled={isChangeDisabled}>
      <StyledIconWrapperDiv $isEnabled={isEnabled}>
        <Icon
          name={resourceConfig?.customIconName ?? getIconName(resourceType)}
          color={isEnabled ? theme.greys.white : theme.greys.shade60}
        />
      </StyledIconWrapperDiv>
      <StyledTitleAndDescriptionWrapperDiv>
        <StyledTitleDiv $isEnabled={isEnabled}>
          {resourceConfig?.customTitle ?? renderTitle(resourceType)}
        </StyledTitleDiv>
        <StyledDescriptionDiv $isEnabled={isEnabled}>
          {resourceConfig?.customDescription ?? renderDescription(resourceType)}
        </StyledDescriptionDiv>
      </StyledTitleAndDescriptionWrapperDiv>
      <StyledToggleWrapperDiv $isAdmin={isAdmin}>
        <Toggle isChecked={isEnabled} onChange={onChange} />
      </StyledToggleWrapperDiv>
    </StyledSectionHeaderWrapperDiv>
  );
};

/*
 * Helpers.
 */

function getIconName(resourceType: CrmSyncIntegrationResourceTypesEnum): IconName {
  if (resourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT) {
    return 'briefcaseFilled';
  }
  return 'contactsCutout';
}

function renderTitle(resourceType: CrmSyncIntegrationResourceTypesEnum) {
  if (resourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT) {
    return <Trans id="crm-syncs-page-accounts-section-title">Sync accounts</Trans>;
  }
  return <Trans id="crm-syncs-page-contacts-section-title">Sync contacts</Trans>;
}

function renderDescription(resourceType: CrmSyncIntegrationResourceTypesEnum) {
  if (resourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT) {
    return (
      <Trans id="crm-syncs-page-accounts-section-description">
        Use account data in rules and to get context in the contact details plugin
      </Trans>
    );
  }
  return (
    <Trans id="crm-syncs-page-contacts-section-description">
      Use contact data in rules and to get context in the contact details plugin
    </Trans>
  );
}
